import { motion } from 'framer-motion';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { BorderCard } from '../components/BorderCard';
import { ExternalLink, ClipboardCheck, FileSpreadsheet, DollarSign, Clock, Package, Megaphone, FileText, TrendingUp } from 'lucide-react';

// ==========================================
// 1. DAILY CHECKLISTS (Unchanged)
// ==========================================
const checklists = [
  { 
    id: 'opening', 
    title: 'Opening Checklist', 
    icon: ClipboardCheck,
    url: 'https://docs.google.com/forms/d/e/1FAIpQLSfSRowKC07y-vHfQhnDXdDBo0WMLb_N5e1t3n4OaPeCMYRoSA/viewform'
  },
  { 
    id: 'closing', 
    title: 'Closing Checklist', 
    icon: ClipboardCheck,
    url: 'https://docs.google.com/forms/d/e/1FAIpQLSfOfvTME2J-ycVF0974JnJA1snMofHKCsrO5ucjoXgTWm3EWw/viewform'
  },
  { 
    id: 'vm', 
    title: 'VM Checklist', 
    icon: ClipboardCheck,
    url: 'https://docs.google.com/forms/d/e/1FAIpQLSct-BAcz5bEla4vG9K9jWPXGgo6qmtrby3cSzN3vJXvkJ1yQQ/viewform'
  },
];

// ==========================================
// 2. CLOSING REPORT (Restructured)
// ==========================================
const closingReports = [
  { 
    id: 'dsr', 
    title: 'DSR', 
    subtitle: 'Daily Sales Report',
    icon: FileSpreadsheet, 
    url: 'https://customer-assets.emergentagent.com/job_luxury-academy-2/artifacts/f4gjud0a_Store%20DSR%202026-27.xlsx',
    lastUpdated: 'FY 2026-27'
  },
  { 
    id: 'tender-collection', 
    title: 'Tender Collection', 
    subtitle: 'End of day collection summary',
    icon: DollarSign, 
    url: 'https://customer-assets.emergentagent.com/job_luxury-academy-2/artifacts/y52n7z3i_Tender%20Collection%202026-27.xlsx',
    lastUpdated: 'FY 2026-27'
  },
  { 
    id: 'hourly-sales', 
    title: 'Hourly Sales Report', 
    subtitle: 'Store hourly performance',
    icon: Clock, 
    url: 'https://customer-assets.emergentagent.com/job_luxury-academy-2/artifacts/jnpu4vi1_Hourly%20Sales%20Report%20Stores.xlsx',
    lastUpdated: 'FY 2026-27'
  },
];

// ==========================================
// 3. SOPs (Operational Guides & Trackers)
// ==========================================
const sopItems = [
  // Trackers (Excel files)
  {
    id: 'inventory-tracker',
    title: 'Inventory Tracker',
    subtitle: 'Stock Management',
    icon: Package,
    url: 'https://customer-assets.emergentagent.com/job_luxury-academy-2/artifacts/cgvymw7u_Inventory%20Tracker.xlsx',
    lastUpdated: 'FY 2026-27',
    type: 'tracker'
  },
  {
    id: 'marketing-pickup',
    title: 'Marketing Pickup',
    subtitle: 'Marketing Materials',
    icon: Megaphone,
    url: 'https://customer-assets.emergentagent.com/job_luxury-academy-2/artifacts/rx0wrdoq_Marketing%20Pick%20up.xlsx',
    lastUpdated: 'FY 2026-27',
    type: 'tracker'
  },
  {
    id: 'petty-cash',
    title: 'Petty Cash',
    subtitle: 'Cash Management Guide',
    icon: FileText,
    url: 'https://customer-assets.emergentagent.com/job_luxury-academy-2/artifacts/glggkgkt_Petty%20Cash%20-2026-27.xlsx',
    lastUpdated: 'FY 2026-27',
    type: 'tracker'
  },
  {
    id: 'weekly-sales',
    title: 'Weekly Sales Report',
    subtitle: 'Store Weekly Performance',
    icon: TrendingUp,
    url: 'https://customer-assets.emergentagent.com/job_luxury-academy-2/artifacts/h2rk1t9g_Weekly%20Sales%20Report-Store.xlsx',
    lastUpdated: 'FY 2026-27',
    type: 'tracker'
  },
  // Guides (Canva links)
  {
    id: 'store-grn',
    title: 'Store GRN',
    subtitle: 'Goods Receipt Note Guide',
    icon: FileText,
    url: 'https://www.canva.com/design/DAHDoTClvMo/oAPWIzbs5y899DSZ3jXhzg/view?utm_content=DAHDoTClvMo&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h802e99689c',
    type: 'guide'
  },
  {
    id: 'inter-store-transfer',
    title: 'Inter Store Transfer',
    subtitle: 'Transfer Process Guide',
    icon: FileText,
    url: 'https://www.canva.com/design/DAHDoVRS3AI/j5Wdv_Lq5KhJSNFZat6vxg/view?utm_content=DAHDoVRS3AI&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=ha307731ae8',
    type: 'guide'
  },
  {
    id: 'cycle-count',
    title: 'Cycle Count & Stock Audit',
    subtitle: 'Inventory Audit Guide',
    icon: Package,
    url: 'https://www.canva.com/design/DAHDoZ9RuMo/BVrwE9T4tIuswXhjlezBrg/view?utm_content=DAHDoZ9RuMo&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h273c06184f',
    type: 'guide'
  },
  {
    id: 'shrinkage-pilferage',
    title: 'Shrinkage & Pilferage Control',
    subtitle: 'Loss Prevention Guide',
    icon: FileText,
    url: 'https://www.canva.com/design/DAHDoQNj0yc/53O_46bVSRerpF1WioBJiw/view?utm_content=DAHDoQNj0yc&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h066ddb6e5d',
    type: 'guide'
  },
];

// ==========================================
// COMPONENTS
// ==========================================

// Clickable checklist card
const ChecklistCard = ({ item }) => {
  const Icon = item.icon;
  
  return (
    <a 
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
      data-testid={`checklist-${item.id}`}
    >
      <BorderCard className="p-8 group">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 border border-[rgba(26,26,26,0.1)] flex items-center justify-center group-hover:border-[#D32F2F] group-hover:bg-[#D32F2F]/5 transition-all duration-500">
            <Icon size={24} strokeWidth={1.5} className="text-[#1A1A1A] group-hover:text-[#D32F2F] transition-colors duration-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm uppercase tracking-[0.1em] font-semibold text-[#1A1A1A]">
              {item.title}
            </h3>
            <p className="text-xs text-[#D32F2F]/70 mt-1">
              Click to open form
            </p>
          </div>
          <ExternalLink size={16} className="text-[#1A1A1A]/30 group-hover:text-[#D32F2F] transition-colors duration-300" />
        </div>
      </BorderCard>
    </a>
  );
};

// Closing Report Card (clickable with Excel files)
const ClosingReportCard = ({ item }) => {
  const Icon = item.icon;
  
  return (
    <a 
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-full"
      data-testid={`closing-${item.id}`}
    >
      <BorderCard className="p-6 text-center group h-full flex flex-col">
        <div className="w-12 h-12 mx-auto mb-4 border border-[rgba(26,26,26,0.1)] flex items-center justify-center group-hover:border-[#D32F2F] group-hover:bg-[#D32F2F]/5 transition-all duration-500">
          <Icon size={20} strokeWidth={1.5} className="text-[#1A1A1A] group-hover:text-[#D32F2F] transition-colors duration-500" />
        </div>
        <h4 className="text-sm uppercase tracking-[0.1em] font-semibold text-[#1A1A1A] mb-1 group-hover:text-[#D32F2F] transition-colors duration-300">
          {item.title}
        </h4>
        <p className="text-xs text-[#1A1A1A]/50 mb-3 flex-1">
          {item.subtitle}
        </p>
        <div className="mt-auto">
          <div className="flex items-center justify-center gap-2 text-[10px] text-[#D32F2F]/70">
            <ExternalLink size={12} />
            <span>Click to view report</span>
          </div>
          {item.lastUpdated && (
            <p className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/40 mt-2">
              Last updated: {item.lastUpdated}
            </p>
          )}
        </div>
      </BorderCard>
    </a>
  );
};

// SOP Card (for operational guides & trackers)
const SOPCard = ({ item }) => {
  const Icon = item.icon;
  
  return (
    <a 
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-full"
      data-testid={`sop-${item.id}`}
    >
      <BorderCard className="p-5 group h-full">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 border border-[rgba(26,26,26,0.1)] flex items-center justify-center group-hover:border-[#D32F2F] group-hover:bg-[#D32F2F]/5 transition-all duration-500 flex-shrink-0">
            <Icon size={18} strokeWidth={1.5} className="text-[#1A1A1A] group-hover:text-[#D32F2F] transition-colors duration-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm uppercase tracking-[0.1em] font-semibold text-[#1A1A1A] group-hover:text-[#D32F2F] transition-colors duration-300 truncate">
              {item.title}
            </h4>
            <p className="text-[10px] text-[#1A1A1A]/50 mt-1">
              {item.subtitle}
            </p>
          </div>
          <ExternalLink size={14} className="text-[#1A1A1A]/30 group-hover:text-[#D32F2F] transition-colors duration-300 flex-shrink-0" />
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1 text-[10px] text-[#D32F2F]/60">
            <ExternalLink size={10} />
            <span>{item.type === 'tracker' ? 'Click to view report' : 'Click to view guide'}</span>
          </div>
          {item.lastUpdated && (
            <p className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/40">
              {item.lastUpdated}
            </p>
          )}
        </div>
      </BorderCard>
    </a>
  );
};

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================
const SOPPage = () => {
  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what" label="Back" />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Training Module
          </h2>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A]">
            Standard Operating Procedures
          </h1>
        </motion.div>

        {/* ========================================== */}
        {/* 1. DAILY CHECKLISTS */}
        {/* ========================================== */}
        <FadeIn delay={0.2}>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
            Daily Checklists
          </h3>
        </FadeIn>
        
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {checklists.map((item) => (
            <StaggerItem key={item.id}>
              <ChecklistCard item={item} />
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* ========================================== */}
        {/* 2. CLOSING REPORT */}
        {/* ========================================== */}
        <FadeIn delay={0.3}>
          <div className="border-t border-[rgba(26,26,26,0.1)] pt-16 mb-16">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
              Closing Report
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {closingReports.map((item) => (
                <ClosingReportCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </FadeIn>

        {/* ========================================== */}
        {/* 3. SOPs (Operational Guides & Trackers) */}
        {/* ========================================== */}
        <FadeIn delay={0.4}>
          <div className="border-t border-[rgba(26,26,26,0.1)] pt-16">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
              SOP's
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sopItems.map((item) => (
                <SOPCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </FadeIn>
      </div>
    </PageTransition>
  );
};

export default SOPPage;
